import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import injectSheet from 'react-jss';
import classNames from 'classnames';

import * as bucketSelectors from '../../selectors/bucketSelectors';
import * as organizationSelectors from '../../selectors/organizationSelectors';
import * as organizationConstants from '../../constants/organizations';
import Bucket from '../../components/bucket/Bucket';
import NothingFound from '../../components/NothingFound';
import Actions from './Actions';
import InfiniteScroll from 'react-infinite-scroll-component';
import NewBucketModal from './NewBucketModal';
import { IconButton, Typography, LinearProgress } from '@material-ui/core';
import DashboardVideoModal from '../../components/modals/DashboardVideoModal';

class OrganizationBuckets extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isDialogOpen: false,
            showDashboardVideoModal: false,
        };

        // opener button DOM node for focus return
        this.openButtonEl = null;
        this.setOpenButtonEl = (el) => { this.openButtonEl = el; };

        this.onLoadMore = this.onLoadMore.bind(this);
        this.onDialogOpen = this.onDialogOpen.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
        this.onOpenDashboardVideo = this.onOpenDashboardVideo.bind(this);
        this.onCloseDashboardVideo = this.onCloseDashboardVideo.bind(this);
    }

    onLoadMore() {
        const { organizationId, filterName, bucketPage } = this.props;
        this.props.onLoadMore(organizationId, bucketPage + 1, filterName);
    }

    onDialogOpen() { this.setState({ isDialogOpen: true }); }
    onDialogClose() { this.setState({ isDialogOpen: false }); }

    onOpenDashboardVideo() {
        this.setState({
            showDashboardVideoModal: true,
        });
    }

    onCloseDashboardVideo() {
        this.setState({ showDashboardVideoModal: false });
    }

    render() {
        const {
            isLoadingBuckets,
            areThereMorePages,
            buckets,
            filterName,
            classes,
            isOrganizationAdmin,
        } = this.props;

        const { isDialogOpen, showDashboardVideoModal } = this.state;
        const iconClasses = classNames('material-icons', 'large', classes.icon);

        const videoMessage = (
            <div className={classes.video}>
                <IconButton
                    className={classes.videoButton}
                    onClick={this.onOpenDashboardVideo}
                    onKeyDown={(e) => {
                        const isEnter = e.key === 'Enter' || e.keyCode === 13;
                        const isSpace = e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 32;
                        if (isEnter || isSpace) {
                            e.preventDefault();
                            this.onOpenDashboardVideo();
                        }
                    }}
                    buttonRef={this.setOpenButtonEl}
                >
                    <i className={iconClasses}>play_circle_outline</i>
                </IconButton>
                <Typography className={classes.videoText}>
                    Having trouble using the dashboard? You can{' '}
                    <a className={classes.clickHere} onClick={this.onOpenDashboardVideo}>
                        click here
                    </a>{' '}
                    to watch our <b>Get started video</b> and learn more.
                </Typography>
            </div>
        );

        let message, loader, actions;
        switch (true) {
            case isLoadingBuckets:
                actions = <Actions />;
                loader = <LinearProgress />;
                break;
            case !buckets.length && !!filterName:
                actions = <Actions />;
                message = <NothingFound />;
                break;
            case !buckets.length && !isOrganizationAdmin:
                message = (
                    <div>
                        <div className={classes.message}>
                            <div className={classes.innerMessage}>
                                <img
                                    alt="information"
                                    src="/assets/no_projects_non_admin.svg"
                                    className={classes.image}
                                />
                                <div>
                                    <Typography className={classes.highlightOrange}>
                                        Oooops!
                                    </Typography>{' '}
                                    <Typography className={classes.subtitle}>
                                        Seems like you are not a member of any project.
                                    </Typography>
                                    <Typography>
                                        Please contact your organization admin for more details.
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        <div>{videoMessage}</div>
                    </div>
                );
                break;
            case !buckets.length && isOrganizationAdmin:
                message = (
                    <div>
                        <div className={classes.message}>
                            <div className={classes.innerMessage}>
                                <img
                                    alt="information"
                                    src="/assets/no_projects.svg"
                                    className={classes.image}
                                />
                                <div>
                                    <Typography className={classes.highlight}>
                                        Welcome!
                                    </Typography>{' '}
                                    <Typography className={classes.subtitle}>
                                        This is your space for creating projects.
                                    </Typography>
                                    <Typography>
                                        To start a new project click on <b>the green button (+)</b> above or{' '}
                                        <a className={classes.clickHere} onClick={this.onDialogOpen}>
                                            click here.
                                        </a>
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        <div>{videoMessage}</div>
                    </div>
                );
                break;
            default:
                actions = <Actions />;
                message = videoMessage;
        }

        const bucketList = buckets
            .filter(bucket => bucket !== undefined)
            .map(bucket => <Bucket key={bucket.id} bucket={bucket} />);

        return (
            <div className={classes.wrapper}>
                {/* Reusable dialog-based Wistia modal */}
                <DashboardVideoModal
                    show={showDashboardVideoModal}
                    onClose={this.onCloseDashboardVideo}
                    videoId="sgtvqidgd6"
                    returnFocusEl={this.openButtonEl}
                />

                <div className={classes.holder}>
                    {loader}
                    {message}
                    {actions}

                    <InfiniteScroll
                        className={classes.infiniteScroll}
                        dataLength={buckets.length}
                        next={this.onLoadMore}
                        hasMore={areThereMorePages}
                        loader={<span> </span>}
                    >
                        <div className={classes.list}>{bucketList}</div>
                    </InfiniteScroll>

                    <NewBucketModal show={isDialogOpen} onClose={this.onDialogClose} />
                </div>
            </div>
        );
    }
}

OrganizationBuckets.propTypes = {
    buckets: PropTypes.array,
    isLoadingBuckets: PropTypes.bool,
    isOrganizationAdmin: PropTypes.bool,
    onLoadMore: PropTypes.func,
    filterName: PropTypes.string,
    bucketPage: PropTypes.number,
    organizationId: PropTypes.string,
    classes: PropTypes.object,
};

OrganizationBuckets.defaultProps = {
    buckets: [],
    isLoadingBuckets: true,
    isOrganizationAdmin: false,
};

const style = ({ spacing, palette }) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 6,
    },
    highlight: {
        fontSize: 26,
        fontWeight: 'bold',
        marginTop: 16,
        marginBottom: 12,
    },
    highlightOrange: {
        color: '#F7931D',
        fontSize: 26,
        fontWeight: 'bold',
        marginTop: 16,
        marginBottom: 12,
    },
    subtitle: {
        fontSize: 15,
    },
    holder: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: spacing.maxWidth,
    },
    list: {
        display: 'flex',
        flexGrow: 1,
        flexWrap: 'wrap',
        marginTop: spacing.unit * 6,
    },
    message: {
        background: 'white',
        width: 960,
        height: 350,
        borderRadius: '4px',
        marginTop: spacing.unit * 6,
    },
    innerMessage: {
        margin: '0 auto',
        textAlign: 'center',
    },
    errorBubble: {
        border: `1px dashed ${palette.error.secondary}`,
        backgroundColor: palette.error.main,
        '& span': {
            color: palette.error.secondary,
        },
    },
    image: {
        marginTop: 49,
    },
    infiniteScroll: {
        // Fix: By default, the component hides overflow which cuts BucketOptionsModal off screen.
        overflow: 'visible !important',
    },
    clickHere: {
        cursor: 'pointer',
        color: palette.primary.main,
        fontWeight: 'bold',
        textDecoration: 'underline',
    },
    video: {
        background: 'rgb(247, 253, 253)',
        borderRadius: 4,
        border: '1px dashed rgb(0, 169, 157)',
        height: 70,
        width: 960,
        marginTop: 15,
        display: 'flex',
    },
    videoButton: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    videoText: {
        color: 'rgb(26, 26, 26)',
        fontSize: 14,
        fontWeight: 'normal',
        height: 20,
        letterSpacing: 0,
        marginTop: 25,
        textAlign: 'center',
        width: 690,
        marginLeft: 45,
    },
    icon: {
        color: palette.primary.main,
        fontSize: '2em',
        marginTop: 35,
        marginLeft: 100,
    },
});

const mapStateToProps = state => ({
    buckets: bucketSelectors.getBuckets(state),
    isLoadingBuckets: bucketSelectors.getIsLoadingBuckets(state),
    areThereMorePages: bucketSelectors.areThereMorePages(state),
    filterName: bucketSelectors.getOrganizationBucketsFilterName(state),
    bucketPage: bucketSelectors.getCurrentPage(state),
    organizationId: organizationSelectors.getCurrentOrganizationId(state),
    isOrganizationAdmin: organizationSelectors.getIsCurrentUserAdminForOrganization(state),
});

const mapDispatchToProps = dispatch => ({
    onLoadMore: (organizationId, page, filterName) => {
        dispatch({
            type: organizationConstants.FETCH_ORGANIZATION_BUCKETS_REQUEST,
            payload: { organizationId, page, filterName },
        });
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(injectSheet(style)(OrganizationBuckets));



// WEBPACK FOOTER //
// ./src/containers/organizationBuckets/OrganizationBuckets.js